import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
} from '@mui/material';
import { ScrollToTop } from '../../../common/table/helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { ReactComponent as CancelIcon } from '@lmt/unicorn-shared/app/icons/cancel.svg';
import { IconButton } from '../../../common/ui-kit/IconButton';
import { snakeCase } from 'lodash';
import { Input } from '../../../common/ui-kit/inputs/Input';
import { validateField } from './validation';
import { AdditionalVisitDto } from '@lmt/shared-types';
import styles from './AdditionalVisitTable.module.scss';
import { TimePicker } from '../../../common/ui-kit/inputs/date-time/TimePicker';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { VisitType } from '../../../calendar/visit/common/types';
import { UniLink } from '../../../common/ui-kit/buttons/UniLink';
import { HL7SettingsFormProps } from './types';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useTranslation } from 'react-i18next';

const tableKeys: { [dictionary: string]: string } = {
  type: 'type',
  startTime: 'start_time',
  visitLength: 'visit_length',
  visitType: 'visit_type',
  options: 'options',
};

const columnKeys = [
  tableKeys.type,
  tableKeys.startTime,
  tableKeys.visitLength,
  tableKeys.visitType,
];

const columnStyles = {
  [tableKeys.type]: {
    minWidth: '140px',
    width: '20%',
  },
  [tableKeys.startTime]: {
    minWidth: '120px',
    width: '120px',
  },
  [tableKeys.visitLength]: {
    minWidth: '120px',
    width: '25%',
  },
  [tableKeys.visitType]: {
    minWidth: '35px',
    width: '32%',
  },
  [tableKeys.options]: {
    minWidth: '32px',
    width: '32px',
  },
};

export const AdditionalVisitTable: FC<HL7SettingsFormProps> = React.memo(
  function AdditionalVisitTable(props) {
    const i18next = useScopedTranslation(
      'tenant_settings.hl7.incoming.visit_creating',
    );
    const i18nextGlobal = useTranslation();
    const nameof = nameofFactory<AdditionalVisitDto>();

    const { watch, setValue } = props.form;
    const [additionalVisits] = watch(['additionalVisits']);

    const tableRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [additionalVisits]);

    const visitTypes = useActiveVisitTypes();
    const translatedVisitTypes: VisitType[] = useMemo(
      () =>
        visitTypes.map((type) => ({
          id: type.id,
          label: i18nextGlobal.t(type.value as any),
        })),
      [visitTypes],
    );

    const onRemove = useCallback(
      (index: number) => {
        if (!additionalVisits) {
          return;
        }
        setValue(
          'additionalVisits',
          additionalVisits.filter((_d, i) => i !== index),
          {
            shouldDirty: true,
          },
        );
      },
      [additionalVisits],
    );

    const onEdit = useCallback(
      (index: number, visit: AdditionalVisitDto) => {
        if (!additionalVisits) {
          return;
        }
        setValue(
          'additionalVisits',
          additionalVisits.map((d, i) => (i === index ? visit : d)),
          {
            shouldDirty: true,
          },
        );
      },
      [additionalVisits],
    );

    const onAdd = useCallback(() => {
      const newVisit: AdditionalVisitDto = {
        type: '',
        visitTypeId: translatedVisitTypes[0]?.id ?? 0,
        visitStartTimeInSecondsSinceMidnight: 61200,
        visitLengthInSeconds: 7200,
      };
      setValue('additionalVisits', [...(additionalVisits ?? []), newVisit], {
        shouldDirty: true,
      });
    }, [additionalVisits, translatedVisitTypes]);

    const header = (
      <div className={styles.tableHeader}>
        <div className={styles.tableTitle}>{i18next.t('title')}</div>
        <div>
          <UniLink title={i18next.t('add_button')} onClick={onAdd} />
        </div>
      </div>
    );

    return (
      <div className={styles.container}>
        {header}
        <TableContainer
          ref={tableRef}
          className={styles.tableContainer}
          component={Paper}
        >
          <Table stickyHeader className={styles.table}>
            <TableHead>
              <TableRow>
                {columnKeys.map((columnKey) => (
                  <TableCell
                    key={`${columnKey}`}
                    style={columnStyles[columnKey]}
                  >
                    <div className={styles.header}>
                      {i18next.t(`${snakeCase(columnKey)}` as any)}
                    </div>
                  </TableCell>
                ))}
                <TableCell
                  className={styles.header}
                  key={tableKeys.options}
                  style={columnStyles[tableKeys.options]}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(additionalVisits ?? []).map((data, index) => (
                <TableRow key={index} hover style={{ minWidth: '1000px' }}>
                  <TableCell style={columnStyles[tableKeys.type]}>
                    <div className={styles.cell}>
                      <Input
                        variant="formInput"
                        value={data.type}
                        onChange={(value) =>
                          onEdit(index, {
                            ...data,
                            type: value.currentTarget.value,
                          })
                        }
                        onBlur={() =>
                          onEdit(index, {
                            ...data,
                            type: data.type?.trim() ?? '',
                          })
                        }
                        errorText={validateField(nameof('type'), data.type)}
                      />
                    </div>
                  </TableCell>
                  <TableCell style={columnStyles[tableKeys.startTime]}>
                    <div className={styles.cell}>
                      <TimePicker
                        variant="formInput"
                        timeInMills={
                          data.visitStartTimeInSecondsSinceMidnight
                            ? data.visitStartTimeInSecondsSinceMidnight * 1000
                            : data.visitStartTimeInSecondsSinceMidnight
                        }
                        error={
                          !!validateField(
                            tableKeys.startTime,
                            data.visitStartTimeInSecondsSinceMidnight,
                          )
                        }
                        errorText={validateField(
                          nameof('visitStartTimeInSecondsSinceMidnight'),
                          data.visitStartTimeInSecondsSinceMidnight,
                        )}
                        onTimeChanged={(value) =>
                          onEdit(index, {
                            ...data,
                            visitStartTimeInSecondsSinceMidnight: value
                              ? value / 1000
                              : (value as any),
                          })
                        }
                      />
                    </div>
                  </TableCell>
                  <TableCell style={columnStyles[tableKeys.visitLength]}>
                    <div className={styles.cell}>
                      <Input
                        variant="formInput"
                        value={data.visitLengthInSeconds / 3600}
                        type="number"
                        step="any"
                        onChange={(value) =>
                          onEdit(index, {
                            ...data,
                            visitLengthInSeconds:
                              value.currentTarget.valueAsNumber * 3600,
                          })
                        }
                        errorText={validateField(
                          nameof('visitLengthInSeconds'),
                          data.visitLengthInSeconds,
                        )}
                      />
                    </div>
                  </TableCell>
                  <TableCell style={columnStyles[tableKeys.visitType]}>
                    <div className={styles.cell}>
                      <DropDownInput
                        variant={'formInput'}
                        useIdFunctionAsValue={true}
                        options={translatedVisitTypes}
                        value={data.visitTypeId}
                        idFunction={'id'}
                        getOptionLabel={'label'}
                        required={true}
                        onValueChanged={(value) =>
                          onEdit(index, { ...data, visitTypeId: value?.id })
                        }
                      />
                    </div>
                  </TableCell>
                  <TableCell
                    className={styles.optionCell}
                    onClick={(event: any) => event.stopPropagation()}
                    style={columnStyles[tableKeys.options]}
                  >
                    <div className={styles.cell}>
                      <IconButton
                        iconComponent={
                          <CancelIcon className={styles.optionIcon} />
                        }
                        onClick={() => onRemove(index)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  },
);
